import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import RiskBadge from './RiskBadge';
import CallModal from './CallModal';
import { formatCurrency } from '../utils/formatters';
import { SENTIMENT_CONFIG } from '../utils/sentimentAnalysis';

function UrgencyBadge({ urgency }) {
  if (!urgency) {
    return null;
  }

  const config = {
    critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'CRITICAL', dot: 'bg-red-500 animate-pulse' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HIGH', dot: 'bg-orange-500' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'MEDIUM', dot: 'bg-amber-500' },
    low: { bg: 'bg-green-100', text: 'text-green-700', label: 'LOW', dot: 'bg-green-500' },
  };
  const styles = config[urgency];

  if (!styles) {
    return null;
  }

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${styles.bg} ${styles.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
      {styles.label}
    </span>
  );
}

function RecoveryBar({ probability, ervDollars }) {
  if (!probability) {
    return null;
  }

  const color = probability >= 70 ? '#0F6E56' : probability >= 40 ? '#EF9F27' : '#ef4444';

  return (
    <div className="flex items-center gap-2 mt-2 ml-7">
      <span className="text-[10px] text-gray-400 w-20 shrink-0">Recovery odds</span>
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${probability}%`, backgroundColor: color }} />
      </div>
      <span className="text-[10px] font-bold tabular-nums" style={{ color }}>{probability}%</span>
      {ervDollars > 0 && (
        <span className="text-[10px] text-gray-400 ml-1">
          Approx. <span className="font-semibold text-gray-600">{formatCurrency(ervDollars)}</span>
        </span>
      )}
    </div>
  );
}

function FactorPill({ label, impact }) {
  const styles = {
    positive: 'bg-green-50 text-green-700 border-green-200',
    negative: 'bg-red-50 text-red-600 border-red-200',
    neutral: 'bg-gray-50 text-gray-500 border-gray-200',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${styles[impact] || styles.neutral}`}>
      {impact === 'positive' ? '+' : impact === 'negative' ? '-' : '.'} {label}
    </span>
  );
}

function PipelinePill({ stage }) {
  if (!stage) {
    return null;
  }

  const config = {
    predict: { label: 'Predict', bg: 'bg-blue-50 text-blue-600 border-blue-200' },
    prioritize: { label: 'Prioritize', bg: 'bg-amber-50 text-amber-600 border-amber-200' },
    execute: { label: 'Execute', bg: 'bg-green-50 text-tenafi-green border-tenafi-green/30' },
  };
  const styles = config[stage];

  if (!styles) {
    return null;
  }

  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${styles.bg}`}>
      {styles.label}
    </span>
  );
}

export default function ActionFeedCard({
  tenant,
  propertyId,
  propertyName,
  isSelected,
  onToggleSelect,
  onSendSMS,
  onOfferPlan,
  erv,
}) {
  const [expanded, setExpanded] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const { risk, recommendation, insight } = tenant;

  const driftLabel = {
    worsening: 'Worsening',
    slightly_worsening: 'Drifting Later',
    improving: 'Improving',
    slightly_improving: 'Improving',
    stable: 'Stable',
  }[tenant.drift?.direction] || 'N/A';

  const driftColor = {
    worsening: 'text-red-500',
    slightly_worsening: 'text-amber-500',
    improving: 'text-green-600',
    slightly_improving: 'text-green-500',
    stable: 'text-gray-400',
  }[tenant.drift?.direction] || 'text-gray-400';

  if (risk?.tier === 'minimal' && (!tenant.currentBalance || tenant.currentBalance <= 0)) {
    return null;
  }

  const sentiment = erv?.lastSentiment;
  const sentimentConfig = sentiment ? SENTIMENT_CONFIG[sentiment.sentiment] : null;

  return (
    <>
      <div
        className={
          'bg-white rounded-2xl border transition-all ' +
          (isSelected ? 'border-tenafi-green shadow-md ring-2 ring-tenafi-green/20' : 'border-gray-200 hover:shadow-md')
        }
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={!!isSelected}
              onChange={onToggleSelect}
              className="mt-1.5 accent-tenafi-green w-4 h-4 flex-shrink-0 cursor-pointer"
            />

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/tenant/${propertyId}/${tenant.id}`}
                  className="text-lg font-semibold text-gray-900 hover:text-tenafi-green transition-colors"
                >
                  Unit {tenant.unit} - {tenant.name}
                </Link>
                <RiskBadge score={risk?.score} tier={risk?.tier} label={risk?.label} />
                {erv && <UrgencyBadge urgency={erv.urgency} />}
                {erv && <PipelinePill stage={erv.pipelineStage} />}
                {tenant.preferredLanguage === 'es' && (
                  <span className="text-xs bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5 rounded-full">ES</span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                <span className="font-medium text-gray-700">Balance: {formatCurrency(tenant.currentBalance)}</span>
                <span className="text-gray-400">Rent: {formatCurrency(tenant.monthlyRent)}</span>
                {tenant.currentDaysLate > 0 && (
                  <span className="text-red-500 font-medium">{tenant.currentDaysLate} days late</span>
                )}
                <span className={`font-medium ${driftColor}`}>{driftLabel}</span>
                {sentimentConfig && (
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${sentimentConfig.bg} ${sentimentConfig.text} ${sentimentConfig.border}`}>
                    {sentimentConfig.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          {erv && <RecoveryBar probability={erv.recoveryProbability} ervDollars={erv.ervDollars} />}

          <p className="mt-3 text-sm text-gray-600 leading-relaxed sm:ml-7">{insight}</p>

          {erv && erv.recommendedAction && (
            <div className="mt-3 sm:ml-7 bg-blue-50 border border-blue-200/60 rounded-xl px-4 py-3">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">AI Action: </span>
                {erv.recommendedAction}
              </p>
            </div>
          )}

          {recommendation && recommendation.action !== 'No action needed' && !erv && (
            <div className="mt-3 sm:ml-7 bg-tenafi-light border border-tenafi-green/20 rounded-xl px-4 py-3">
              <p className="text-sm text-tenafi-green-dark">
                <span className="font-semibold">Recommended: </span>
                {recommendation.description}
              </p>
            </div>
          )}

          {recommendation?.buttons?.length > 0 && (
            <div className="mt-4 sm:ml-7 flex flex-wrap gap-2">
              {recommendation.buttons.map((buttonLabel) => (
                <button
                  key={buttonLabel}
                  onClick={() => {
                    if (buttonLabel === 'Send SMS' || buttonLabel === 'Send Email') onSendSMS?.(tenant);
                    if (buttonLabel === 'Offer Payment Plan' || buttonLabel === 'Offer Split Payment') onOfferPlan?.(tenant);
                    if (buttonLabel === 'Call Tenant') setCallOpen(true);
                  }}
                  className={
                    'px-4 py-2.5 min-h-[44px] rounded-xl text-sm font-medium transition-colors ' +
                    (buttonLabel === 'Call Tenant'
                      ? 'bg-tenafi-amber text-white hover:bg-tenafi-amber-dark'
                      : buttonLabel === 'Send SMS'
                        ? 'bg-tenafi-green text-white hover:bg-tenafi-green-dark'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50')
                  }
                >
                  {buttonLabel}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 sm:ml-7 text-xs text-gray-400 hover:text-gray-600 transition-colors min-h-[36px] flex items-center"
          >
            {expanded ? 'Hide details' : 'Show details'}
          </button>

          {expanded && (
            <div className="mt-3 sm:ml-7 pt-3 border-t border-gray-100 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recent Payments</p>
                <div className="space-y-1.5">
                  {(tenant.paymentHistory || []).slice(-4).reverse().map((payment, index) => (
                    <div key={index} className="flex justify-between text-xs text-gray-500">
                      <span>{new Date(payment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span className="font-medium">{formatCurrency(payment.amount)}</span>
                      <span className={payment.daysLate > 5 ? 'text-red-500' : 'text-green-600'}>
                        {payment.amount === 0 ? 'Missed' : payment.daysLate > 0 ? `${payment.daysLate}d late` : 'On time'}
                      </span>
                    </div>
                  ))}
                </div>
                {tenant.fingerprint?.medianPayDay && (
                  <p className="mt-2 text-xs text-gray-400">
                    Typically pays day {tenant.fingerprint.medianPayDay} - Next expected: {tenant.fingerprint.expectedNextPayDate || 'N/A'} - {tenant.fingerprint.consistency?.replace('_', ' ')}
                  </p>
                )}
              </div>

              {erv?.factors?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    ERV Factors <span className="normal-case text-gray-400 font-normal">(why this score)</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {erv.factors.slice(0, 7).map((factor, index) => (
                      <FactorPill key={index} label={factor.label} impact={factor.impact} />
                    ))}
                  </div>
                </div>
              )}

              {sentiment && (
                <div className={`rounded-xl p-3 border ${sentimentConfig?.bg} ${sentimentConfig?.border}`}>
                  <p className={`text-xs font-semibold mb-1 ${sentimentConfig?.text}`}>
                    Last Response Sentiment
                  </p>
                  <p className="text-xs text-gray-700">{sentiment.summary}</p>
                  {sentiment.signals.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {sentiment.signals.map((signal, index) => (
                        <li key={index} className="text-[10px] text-gray-500">- {signal}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {callOpen && (
        <CallModal
          tenant={tenant}
          propertyId={propertyId}
          propertyName={propertyName}
          onClose={() => setCallOpen(false)}
          onCallLogged={() => setCallOpen(false)}
        />
      )}
    </>
  );
}
